import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Download, FileSpreadsheet, Eye, Settings2 } from 'lucide-react';
import { processCountyData, exportToExcel } from '@/utils/countyDataProcessor';
import { processMercerCountyData, exportMercerToExcel } from '@/utils/mercerCountyDataProcessor';
import { processUniversalCountyData, exportUniversalToExcel, autoDetectColumnMappings, type ColumnMapping as UniversalColumnMapping } from '@/utils/universalCountyDataProcessor';
import { useToast } from '@/hooks/use-toast';
import { ColumnMapper, type ColumnMapping, type ColumnType } from './ColumnMapper';

type CountyFormat = 'universal' | 'standard' | 'mercer';

export const DataExportTool = () => {
  const [rawData, setRawData] = useState('');
  const [format, setFormat] = useState<CountyFormat>('universal');
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showColumnMapper, setShowColumnMapper] = useState(false);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const { toast } = useToast();

  const handleShowColumnMapper = () => {
    if (!rawData.trim()) {
      toast({
        title: "No data",
        description: "Please paste your data first",
        variant: "destructive"
      });
      return;
    }

    // Extract headers from first line
    const lines = rawData.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      toast({
        title: "No data",
        description: "Could not detect any data rows",
        variant: "destructive"
      });
      return;
    }

    const headers = lines[0].split('\t').map(h => h.trim());
    setDetectedColumns(headers);

    // Auto-detect column types
    const autoMappings = autoDetectColumnMappings(headers);
    setColumnMappings(
      autoMappings.map(m => ({
        columnName: m.columnName,
        type: m.type,
        autoDetected: true,
      }))
    );

    setShowColumnMapper(true);
    setShowPreview(false);
  };

  const handleColumnMappingChange = (columnName: string, type: ColumnType) => {
    setColumnMappings(prev =>
      prev.map(m =>
        m.columnName === columnName
          ? { ...m, type, autoDetected: false }
          : m
      )
    );
  };

  const handleApplyMapping = () => {
    try {
      let records;
      if (format === 'universal') {
        const universalMappings: UniversalColumnMapping[] = columnMappings.map(m => ({
          columnName: m.columnName,
          type: m.type,
        }));
        records = processUniversalCountyData(rawData, universalMappings);
      } else if (format === 'mercer') {
        records = processMercerCountyData(rawData);
      } else {
        records = processCountyData(rawData);
      }

      setPreviewData(records);
      setShowPreview(true);
      setShowColumnMapper(false);

      toast({
        title: "Preview ready",
        description: `Parsed ${records.length} records with custom mapping`
      });
    } catch (error) {
      console.error('Preview error:', error);
      toast({
        title: "Error",
        description: "Failed to process data. Please check the format.",
        variant: "destructive"
      });
    }
  };

  const handlePreview = () => {
    if (!rawData.trim()) {
      toast({
        title: "No data",
        description: "Please paste your data first",
        variant: "destructive"
      });
      return;
    }

    try {
      let records;
      if (format === 'universal') {
        records = processUniversalCountyData(rawData);
      } else if (format === 'mercer') {
        records = processMercerCountyData(rawData);
      } else {
        records = processCountyData(rawData);
      }

      setPreviewData(records);
      setShowPreview(true);

      toast({
        title: "Preview ready",
        description: `Parsed ${records.length} records`
      });
    } catch (error) {
      console.error('Preview error:', error);
      toast({
        title: "Error",
        description: "Failed to process data. Please check the format.",
        variant: "destructive"
      });
    }
  };

  const handleExport = () => {
    if (previewData.length === 0) {
      toast({
        title: "No data",
        description: "Please preview the data first",
        variant: "destructive"
      });
      return;
    }

    try {
      if (format === 'universal') {
        exportUniversalToExcel(previewData);
      } else if (format === 'mercer') {
        exportMercerToExcel(previewData);
      } else {
        exportToExcel(previewData);
      }
      
      toast({
        title: "Success!",
        description: `Exported ${previewData.length} records to Excel`
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Error",
        description: "Failed to export data.",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          County Data Export Tool
        </CardTitle>
        <CardDescription>
          Paste your tab-delimited county data to clean, deduplicate, and export to Excel
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={format} onValueChange={(v) => setFormat(v as CountyFormat)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="universal">Universal</TabsTrigger>
            <TabsTrigger value="standard">Standard (Licking, Lorain, Medina)</TabsTrigger>
            <TabsTrigger value="mercer">Mercer County</TabsTrigger>
          </TabsList>

          <TabsContent value="universal" className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Paste Any County Data (tab-separated)</label>
              <Textarea
                value={rawData}
                onChange={(e) => setRawData(e.target.value)}
                placeholder="Column1	Column2	Column3	...&#10;Value1	Value2	Value3	..."
                className="min-h-[200px] font-mono text-xs"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              First row = headers, rest = data. Names are auto-parsed into First/Last. Works with any county format.
            </p>
          </TabsContent>

          <TabsContent value="standard" className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Paste County Data (tab-separated)</label>
              <Textarea
                value={rawData}
                onChange={(e) => setRawData(e.target.value)}
                placeholder="County	DocumentNumber	Date	Names	Address"
                className="min-h-[200px] font-mono text-xs"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Format: County → Document# → Date → Names → Address
            </p>
          </TabsContent>

          <TabsContent value="mercer" className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Paste Mercer County Data (tab-separated)</label>
              <Textarea
                value={rawData}
                onChange={(e) => setRawData(e.target.value)}
                placeholder="Row	...	V	SellerName	BuyerName	Date	DocType	OR	0000	DocNumber	LegalDesc"
                className="min-h-[200px] font-mono text-xs"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Format: Row → ... → V → Seller → Buyer → Date → Type → ... → Doc# → Legal
            </p>
          </TabsContent>
        </Tabs>

        <div className="flex gap-2">
          {format === 'universal' && (
            <Button onClick={handleShowColumnMapper} className="gap-2" variant="secondary">
              <Settings2 className="h-4 w-4" />
              Customize Mapping
            </Button>
          )}
          <Button onClick={handlePreview} className="gap-2 flex-1" variant="outline">
            <Eye className="h-4 w-4" />
            Preview Data
          </Button>
          <Button onClick={handleExport} className="gap-2 flex-1" disabled={!showPreview}>
            <Download className="h-4 w-4" />
            Export to Excel
          </Button>
        </div>

        {showColumnMapper && format === 'universal' && (
          <ColumnMapper
            columns={detectedColumns}
            mappings={columnMappings}
            onMappingChange={handleColumnMappingChange}
            onApply={handleApplyMapping}
            onCancel={() => setShowColumnMapper(false)}
          />
        )}

        {showPreview && previewData.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Preview ({previewData.length} records)</h4>
            <ScrollArea className="h-[400px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    {format === 'universal' ? (
                      // Dynamic headers for universal format
                      Object.keys(previewData[0]).map((key) => (
                        <TableHead key={key}>{key}</TableHead>
                      ))
                    ) : format === 'mercer' ? (
                      <>
                        <TableHead>Row</TableHead>
                        <TableHead>Seller First</TableHead>
                        <TableHead>Seller Last</TableHead>
                        <TableHead>Buyer First</TableHead>
                        <TableHead>Buyer Last</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Doc #</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead>County</TableHead>
                        <TableHead>Doc #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>First</TableHead>
                        <TableHead>Last</TableHead>
                        <TableHead>Street</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>ZIP</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.slice(0, 100).map((record, idx) => (
                    <TableRow key={idx}>
                      {format === 'universal' ? (
                        // Dynamic cells for universal format
                        Object.keys(record).map((key) => (
                          <TableCell key={key} className="text-xs font-mono max-w-[200px] truncate">
                            {String(record[key])}
                          </TableCell>
                        ))
                      ) : format === 'mercer' ? (
                        <>
                          <TableCell className="font-mono text-xs">{record.rowNumber}</TableCell>
                          <TableCell className="text-xs">{record.sellerFirstName}</TableCell>
                          <TableCell className="text-xs">{record.sellerLastName}</TableCell>
                          <TableCell className="text-xs">{record.buyerFirstName}</TableCell>
                          <TableCell className="text-xs">{record.buyerLastName}</TableCell>
                          <TableCell className="font-mono text-xs">{record.date}</TableCell>
                          <TableCell className="font-mono text-xs">{record.documentNumber}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-xs">{record.county}</TableCell>
                          <TableCell className="font-mono text-xs">{record.documentNumber}</TableCell>
                          <TableCell className="font-mono text-xs">{record.date}</TableCell>
                          <TableCell className="text-xs">{record.firstName}</TableCell>
                          <TableCell className="text-xs">{record.lastName}</TableCell>
                          <TableCell className="text-xs">{record.street}</TableCell>
                          <TableCell className="text-xs">{record.city}</TableCell>
                          <TableCell className="text-xs">{record.state}</TableCell>
                          <TableCell className="font-mono text-xs">{record.zip}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            {previewData.length > 100 && (
              <p className="text-xs text-muted-foreground">Showing first 100 of {previewData.length} records</p>
            )}
          </div>
        )}

        <div className="text-xs text-muted-foreground space-y-1">
          <p><strong>Processing steps:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            {format === 'universal' ? (
              <>
                <li>Auto-detects column structure from first row (headers)</li>
                <li>Parses names (seller/buyer/grantor/grantee) into First/Last columns with proper capitalization</li>
                <li>Parses addresses into Street, City, State, ZIP columns with proper formatting</li>
                <li>Exports all data with cleaned formatting to Excel</li>
              </>
            ) : (
              <>
                <li>Parse seller/buyer names into First and Last Name columns</li>
                <li>Remove middle initials and suffixes</li>
                {format === 'standard' && (
                  <>
                    <li>Parse addresses into Street, City, State, ZIP</li>
                    <li>Remove duplicate records and mislabeled counties</li>
                  </>
                )}
                <li>Export clean data to Excel</li>
              </>
            )}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
